// pages/Projects.jsx - UPDATED with Advanced Filters
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  RefreshCw,
  Briefcase,
  Plus,
  MoreVertical,
  Edit,
  Trash2,
  ExternalLink,
  Calendar,
  User,
  Building2,
  AlertCircle,
  Clock,
  TrendingUp,
  CheckCircle,
  Sliders,
} from "lucide-react";
import apiService from "../../services/api";
import CreateProjectModal from "../../components/CreateProjectModal";
import AdvancedProjectFilterModal from "../AdvancedProjectFilterBuilderModal";
import { useTheme } from "../../contexts/ThemeContext";

const Projects = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState({
    totalProjects: 0,
    projectsByStatus: {},
    projectsByPriority: {},
    recentProjects: 0,
  });
  const [loading, setLoading] = useState(true);

  const [pagination, setPagination] = useState(() => {
    const saved = sessionStorage.getItem("projectsPagination");
    return saved
      ? JSON.parse(saved)
      : { page: 1, limit: 10, total: 0, totalPages: 0 };
  });

  const [filters, setFilters] = useState(() => {
    const saved = sessionStorage.getItem("projectsFilters");
    return saved ? JSON.parse(saved) : { status: "", priority: "", search: "" };
  });

  const [advancedFilters, setAdvancedFilters] = useState(() => {
    const saved = sessionStorage.getItem("projectsAdvancedFilters");
    return saved ? JSON.parse(saved) : null;
  });

  const [activePresetId, setActivePresetId] = useState(() => {
    const saved = sessionStorage.getItem("projectsActivePresetId");
    return saved ? JSON.parse(saved) : null;
  });

  const [activePresetName, setActivePresetName] = useState(() => {
    const saved = sessionStorage.getItem("projectsActivePresetName");
    return saved ? JSON.parse(saved) : null;
  });

  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(null);

  useEffect(() => {
    sessionStorage.setItem("projectsFilters", JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    if (advancedFilters) {
      sessionStorage.setItem(
        "projectsAdvancedFilters",
        JSON.stringify(advancedFilters),
      );
    } else {
      sessionStorage.removeItem("projectsAdvancedFilters");
    }
  }, [advancedFilters]);

  useEffect(() => {
    sessionStorage.setItem("projectsPagination", JSON.stringify(pagination));
  }, [pagination]);

  useEffect(() => {
    if (activePresetId) {
      sessionStorage.setItem(
        "projectsActivePresetId",
        JSON.stringify(activePresetId),
      );
    } else {
      sessionStorage.removeItem("projectsActivePresetId");
    }
  }, [activePresetId]);

  useEffect(() => {
    if (activePresetName) {
      sessionStorage.setItem(
        "projectsActivePresetName",
        JSON.stringify(activePresetName),
      );
    } else {
      sessionStorage.removeItem("projectsActivePresetName");
    }
  }, [activePresetName]);

  useEffect(() => {
    fetchProjects();
    fetchStats();
  }, [pagination.page, filters, advancedFilters]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      let response;
      if (advancedFilters) {
        response = await apiService.getProjectsAdvanced({
          page: pagination.page,
          limit: pagination.limit,
          advancedFilters: JSON.stringify(advancedFilters),
          filterPresetId: activePresetId,
        });
      } else {
        response = await apiService.getProjects({
          page: pagination.page,
          limit: pagination.limit,
          ...filters,
        });
      }
      setProjects(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiService.getProjectStats();
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleApplyAdvancedFilters = async (filterConfig, presetId) => {
    setAdvancedFilters(filterConfig);
    setActivePresetId(presetId);
    setPagination((prev) => ({ ...prev, page: 1 }));
    if (presetId) {
      try {
        const response = await apiService.getFilterPreset(presetId);
        setActivePresetName(response.data.name);
      } catch (error) {
        console.error("Error fetching preset name:", error);
      }
    } else {
      setActivePresetName(null);
    }
  };

  const handleClearAdvancedFilters = () => {
    setAdvancedFilters(null);
    setActivePresetId(null);
    setActivePresetName(null);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleCreateSuccess = () => {
    fetchProjects();
    fetchStats();
    setShowCreateModal(false);
  };

  const handleDeleteProject = async (projectId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this project? This action cannot be undone.",
      )
    ) {
      try {
        await apiService.deleteProject(projectId);
        fetchProjects();
        fetchStats();
      } catch (error) {
        console.error("Error deleting project:", error);
        if (error.response?.data?.relatedRecords) {
          alert(
            `Cannot delete project. It has:\n` +
              `${error.response.data.relatedRecords.tasks} tasks\n` +
              `${error.response.data.relatedRecords.broadbandInfos} broadband records\n` +
              `${error.response.data.relatedRecords.portsOrders} ports orders`,
          );
        } else {
          alert("Failed to delete project");
        }
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      "Survey Requested":
        "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
      "Awaiting Survey":
        "bg-orange-500/10 text-orange-400 border-orange-500/20",
      "Survey Booked": "bg-blue-500/10 text-blue-400 border-blue-500/20",
      "Survey Completed": "bg-cyan-500/10 text-cyan-400 border-cyan-500/20",
      Quotation: "bg-purple-500/10 text-purple-400 border-purple-500/20",
      "Awaiting Finance Approval":
        "bg-pink-500/10 text-pink-400 border-pink-500/20",
      Porting: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20",
      "Broadband Install": "bg-teal-500/10 text-teal-400 border-teal-500/20",
      Complete: "bg-green-500/10 text-green-400 border-green-500/20",
      Cancelled: "bg-red-500/10 text-red-400 border-red-500/20",
    };
    return colors[status] || colors["Survey Requested"];
  };

  const getPriorityColor = (priority) => {
    const colors = {
      Low: "text-gray-400",
      Medium: "text-yellow-400",
      High: "text-orange-400",
      Urgent: "text-red-400",
    };
    return colors[priority] || colors["Medium"];
  };

  const formatDate = (date) => {
    if (!date) return "Not set";
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const ProjectRow = ({ project }) => {
    const initials = project.projectName
      ? project.projectName
          .split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
          .substring(0, 2)
      : "PR";

    return (
      <tr
        className={`border-b transition-colors cursor-pointer ${isDarkMode ? "border-gray-700 hover:bg-gray-800/50" : "border-gray-200 hover:bg-gray-50"}`}
        onClick={() => navigate(`/projects/${project.id}`)}
      >
        <td className="py-4 px-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold">
              {initials}
            </div>
            <div>
              <div
                className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {project.projectName}
              </div>
              {project.projectNumber && (
                <div
                  className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {project.projectNumber}
                </div>
              )}
            </div>
          </div>
        </td>
        <td className="py-4 px-4">
          {project.account && (
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-gray-400" />
              <span className={isDarkMode ? "text-gray-300" : "text-gray-600"}>
                {project.account.name}
              </span>
            </div>
          )}
        </td>
        <td className="py-4 px-4">
          {project.status && (
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full border text-xs font-medium ${getStatusColor(project.status)}`}
            >
              {project.status}
            </span>
          )}
        </td>
        <td className="py-4 px-4">
          {project.priority && (
            <span
              className={`text-sm font-medium ${getPriorityColor(project.priority)}`}
            >
              {project.priority}
            </span>
          )}
        </td>
        <td
          className={`py-4 px-4 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
        >
          {formatDate(project.startDate)}
        </td>
        <td className="py-4 px-4">
          {project.projectBlocked ? (
            <div className="flex items-center gap-1 text-red-400">
              <AlertCircle className="w-4 h-4" />
              <span className="text-xs">Blocked</span>
            </div>
          ) : (
            <div className="flex items-center gap-1 text-green-400">
              <CheckCircle className="w-4 h-4" />
              <span className="text-xs">On Track</span>
            </div>
          )}
        </td>
        <td
          className={`py-4 px-4 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
        >
          {project.daysOpen ?? "—"}
        </td>
        <td className="py-4 px-4">
          {project.owner ? (
            <div className="flex items-center gap-2">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium overflow-hidden ${isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-600"}`}
              >
                {project.owner.profileImageUrl ? (
                  <img
                    src={project.owner.profileImageUrl}
                    alt={project.owner.name}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  project.owner.name?.charAt(0).toUpperCase()
                )}
              </div>
              <span
                className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
              >
                {project.owner.name}
              </span>
            </div>
          ) : (
            <span
              className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
            >
              —
            </span>
          )}
        </td>
        <td className="py-4 px-4">
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowActionsMenu(
                  showActionsMenu === project.id ? null : project.id,
                );
              }}
              className={`p-2 rounded-lg transition-colors ${isDarkMode ? "text-gray-400 hover:text-white hover:bg-gray-700" : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"}`}
            >
              <MoreVertical size={16} />
            </button>

            {showActionsMenu === project.id && (
              <div
                className={`absolute right-0 top-8 border rounded-lg shadow-xl z-10 min-w-[160px] ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/projects/${project.id}`);
                    setShowActionsMenu(null);
                  }}
                  className={`w-full flex items-center gap-2 px-4 py-2 text-left transition-colors text-sm ${isDarkMode ? "text-gray-300 hover:bg-gray-700" : "text-gray-600 hover:bg-gray-50"}`}
                >
                  <ExternalLink size={14} />
                  Open Project
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteProject(project.id);
                    setShowActionsMenu(null);
                  }}
                  className={`w-full flex items-center gap-2 px-4 py-2 text-left text-red-400 transition-colors text-sm ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-50"}`}
                >
                  <Trash2 size={14} />
                  Delete
                </button>
              </div>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div
      className={`min-h-screen font-inter ${isDarkMode ? "bg-[#0C0E12] text-white" : "bg-gray-50 text-gray-900"}`}
    >
      <div className="p-4 lg:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="flex-1 min-w-0">
            <h1
              className={`text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Projects
            </h1>
            <p
              className={`text-sm sm:text-base leading-relaxed ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Manage and track all your projects in one place.
            </p>
          </div>

          <div className="flex flex-col md:flex-row gap-2 sm:gap-3 shrink-0">
            <button
              onClick={fetchProjects}
              className={`flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg transition-colors text-sm sm:text-base font-medium min-w-0 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-gray-300 hover:bg-gray-700" : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"}`}
            >
              <RefreshCw size={16} className="shrink-0" />
              <span className="whitespace-nowrap">Refresh</span>
            </button>

            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-white rounded-lg font-medium transition-all duration-300 relative hover:scale-[1.02] hover:shadow-xl text-sm sm:text-base min-w-0"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
                border: "1px solid",
                borderImageSource:
                  "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                boxShadow: `
                  0px 36px 32px -16px #427BCA36,
                  0px 8px 16px 0px #2F578F1F,
                  0px 2px 4px -8px #427BCA14,
                  0px 8px 24px -16px #FFFFFF3D inset,
                  0px -24px 32px 0px #FFFFFF38 inset,
                  0px 0px 12px 0px #FFFFFF inset
                `,
              }}
            >
              <Plus size={16} className="shrink-0" />
              <span className="whitespace-nowrap">New Project</span>
            </button>
          </div>
        </div>

        {/* Active Advanced-Filter Badge */}
        {advancedFilters && (
          <div className="mb-4 bg-purple-600/10 border border-purple-600/30 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sliders size={20} className="text-purple-400" />
              <div>
                <div className="text-purple-400 font-medium">
                  {activePresetName
                    ? `Filter: ${activePresetName}`
                    : "Advanced Filter Active"}
                </div>
                <div
                  className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {advancedFilters.conditions.length} condition
                  {advancedFilters.conditions.length !== 1 ? "s" : ""} applied
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowAdvancedFilter(true)}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm"
              >
                Edit Filter
              </button>
              <button
                onClick={handleClearAdvancedFilters}
                className={`px-4 py-2 rounded-lg transition-colors text-sm ${isDarkMode ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-200 text-gray-600 hover:bg-gray-300"}`}
              >
                Clear Filter
              </button>
            </div>
          </div>
        )}

        {/* Filters row */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-3">
            {/* Search */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search projects..."
                value={filters.search}
                onChange={(e) =>
                  setFilters({ ...filters, search: e.target.value })
                }
                className={`pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:border-gray-600 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-400" : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"}`}
              />
              <Search
                size={16}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
            </div>

            {/* Status dropdown */}
            <select
              value={filters.status}
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value })
              }
              className={`px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-gray-600 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
            >
              <option value="">All Statuses</option>
              <option value="Survey Requested">Survey Requested</option>
              <option value="Awaiting Survey">Awaiting Survey</option>
              <option value="Survey Booked">Survey Booked</option>
              <option value="Survey Completed">Survey Completed</option>
              <option value="Quotation">Quotation</option>
              <option value="Awaiting Finance Approval">
                Awaiting Finance Approval
              </option>
              <option value="Porting">Porting</option>
              <option value="Broadband Install">Broadband Install</option>
              <option value="Installation">Installation</option>
              <option value="Complete">Complete</option>
              <option value="Cancelled">Cancelled</option>
            </select>

            {/* Priority dropdown */}
            <select
              value={filters.priority}
              onChange={(e) =>
                setFilters({ ...filters, priority: e.target.value })
              }
              className={`px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-gray-600 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
            >
              <option value="">All Priorities</option>
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
              <option value="Urgent">Urgent</option>
            </select>
          </div>

          {/* Advanced Filters button */}
          <div className="flex gap-2">
            <button
              onClick={() => setShowAdvancedFilter(true)}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-white rounded-lg font-medium transition-all duration-300 relative hover:scale-[1.02] hover:shadow-xl text-sm sm:text-base min-w-0"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
                border: "1px solid",
                borderImageSource:
                  "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                boxShadow: `
                  0px 36px 32px -16px #427BCA36,
                  0px 8px 16px 0px #2F578F1F,
                  0px 2px 4px -8px #427BCA14,
                  0px 8px 24px -16px #FFFFFF3D inset,
                  0px -24px 32px 0px #FFFFFF38 inset,
                  0px 0px 12px 0px #FFFFFF inset
                `,
              }}
            >
              <Sliders size={16} className="shrink-0" />
              <span className="whitespace-nowrap">Advanced Filters</span>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            isDarkMode={isDarkMode}
            title="Total Projects"
            value={stats.totalProjects}
            icon={Briefcase}
          />
          <StatCard
            isDarkMode={isDarkMode}
            title="In Progress"
            value={
              (stats.projectsByStatus?.Porting || 0) +
              (stats.projectsByStatus?.["Broadband Install"] || 0)
            }
            icon={Clock}
          />
          <StatCard
            isDarkMode={isDarkMode}
            title="Completed"
            value={stats.projectsByStatus?.Complete || 0}
            icon={CheckCircle}
          />
          <StatCard
            isDarkMode={isDarkMode}
            title="Recent Projects"
            value={stats.recentProjects}
            icon={TrendingUp}
          />
        </div>

        {/* Table */}
        <div
          className={`rounded-lg border mb-8 ${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-white border-gray-200"}`}
        >
          <div
            className={`flex items-center justify-between p-6 border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
          >
            <div>
              <h2
                className={`text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                All Projects{" "}
                <span
                  className={`text-sm ml-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {pagination.total} projects
                </span>
              </h2>
              <p
                className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Track and manage all your projects
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[1200px]">
              <thead className={isDarkMode ? "bg-[#13161B]" : "bg-gray-50"}>
                <tr
                  className={`border-b ${isDarkMode ? "border-[#22262F]" : "border-gray-200"}`}
                >
                  {[
                    "Project Name",
                    "Account",
                    "Status",
                    "Priority",
                    "Start Date",
                    "Project Status",
                    "Days Open",
                    "Owner",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="9"
                      className={`py-8 text-center ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Loading projects...
                    </td>
                  </tr>
                ) : projects.length === 0 ? (
                  <tr>
                    <td
                      colSpan="9"
                      className={`py-8 text-center ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      No projects found
                      {advancedFilters
                        ? " matching your filters. Try adjusting them."
                        : ". Create your first project to get started."}
                    </td>
                  </tr>
                ) : (
                  projects.map((project) => (
                    <ProjectRow key={project.id} project={project} />
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div
            className={`flex flex-col sm:flex-row sm:items-center justify-between p-6 border-t gap-4 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
          >
            <div className="flex gap-2">
              <button
                onClick={() =>
                  setPagination((prev) => ({ ...prev, page: prev.page - 1 }))
                }
                disabled={pagination.page === 1}
                className={`px-3 py-1 border rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? "bg-[#0C0E12] border-[#373A41] text-gray-300 hover:bg-gray-600" : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"}`}
              >
                Previous
              </button>
              <button
                onClick={() =>
                  setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
                }
                disabled={pagination.page >= pagination.totalPages}
                className={`px-3 py-1 border rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? "bg-[#0C0E12] border-[#373A41] text-gray-300 hover:bg-gray-600" : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"}`}
              >
                Next
              </button>
            </div>
            <span
              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Page {pagination.page} of {pagination.totalPages || 1}
            </span>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateProjectModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
      )}

      {showAdvancedFilter && (
        <AdvancedProjectFilterModal
          isOpen={showAdvancedFilter}
          onClose={() => setShowAdvancedFilter(false)}
          onApplyFilters={handleApplyAdvancedFilters}
        />
      )}
    </div>
  );
};

const StatCard = ({ isDarkMode, title, value, icon: Icon }) => (
  <div
    className={`rounded-xl p-6 border transition-colors ${isDarkMode ? "bg-[#0C0E12] border-gray-700/50 hover:border-gray-600/50" : "bg-white border-gray-200 hover:border-gray-300"}`}
  >
    <div className="flex items-center justify-between mb-4">
      <div
        className={`text-[14px] font-medium ${isDarkMode ? "text-[#94979C]" : "text-gray-500"}`}
      >
        {title}
      </div>
      {Icon && <Icon size={20} className="text-gray-400" />}
    </div>
    <div
      className={`text-[28px] font-bold ${isDarkMode ? "text-[#F7F7F7]" : "text-gray-900"}`}
    >
      {value}
    </div>
  </div>
);

export default Projects;
